import { axios } from "./axios";

const BASE_API_URL = process.env.REACT_APP_GRIGORA_API_URL || "http://localhost:2706";

// Get token from localStorage
const getToken = () => {
  return localStorage.getItem("imap_token");
};

// Set token in localStorage
const setToken = (token) => {
  localStorage.setItem("imap_token", token);
};

// Remove token from localStorage
const removeToken = () => {
  localStorage.removeItem("imap_token");
};

// Create axios instance with default config
const mailClientAxios = axios.create({
  baseURL: `${BASE_API_URL}/mail/client`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include token
mailClientAxios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
mailClientAxios.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // Only remove token and redirect on actual 401 errors (not network errors)
    // Network errors (no response) should not trigger logout
    if (error.response && error.response.status === 401) {
      // Don't remove token for verify endpoint - let MailLoginRequired handle it
      const isVerifyEndpoint = error.config?.url?.includes("/verify");
      
      if (!isVerifyEndpoint) {
        // Token expired or invalid, remove it (except for verify calls)
        removeToken();
        // Only redirect if not already on login page
        const currentPath = window.location.pathname;
        if (currentPath !== "/login" && !currentPath.includes("/login")) {
          // Use setTimeout to avoid redirect during render
          setTimeout(() => {
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
          }, 100);
        }
      }
    }
    // For network errors (no response), don't remove token - just reject
    return Promise.reject(error);
  }
);

// API functions
const mailClientAPI = {
  // Login with email and password
  login: async (email, password) => {
    const response = await mailClientAxios.post("/login", {
      email,
      password,
    });
    
    if (response.Output?.token) {
      setToken(response.Output.token);
    }
    
    return response;
  },

  // Logout
  logout: async () => {
    try {
      await mailClientAxios.post("/logout");
    } catch (error) {
      // Even if logout fails, remove token
      console.error("Logout error:", error);
    } finally {
      removeToken();
    }
  },

  // Verify current session
  verify: async () => {
    return await mailClientAxios.get("/verify");
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!getToken();
  },

  // Get token
  getToken: getToken,

  // Get current user info
  getMe: async () => {
    return await mailClientAxios.get("/me");
  },

  // Update user settings
  updateSettings: async (displayName, signature, outOfOffice) => {
    return await mailClientAxios.put("/settings", {
      displayName,
      signature,
      outOfOffice,
    });
  },

  // Get mailboxes/folders
  getMailboxes: async () => {
    return await mailClientAxios.get("/mailboxes");
  },

  // Get specific mailbox
  getMailbox: async (path) => {
    return await mailClientAxios.get(`/mailboxes/${encodeURIComponent(path)}`);
  },

  // Get messages
  getMessages: async (mailbox = "INBOX", page = 1, pageSize = 50, labelId = null) => {
    const params = { mailbox, page, pageSize };
    if (labelId) {
      params.labelId = labelId;
    }
    return await mailClientAxios.get("/messages", { params });
  },

  // Search messages
  searchMessages: async (mailbox = "INBOX", searchParams = {}, page = 1, pageSize = 50) => {
    return await mailClientAxios.get("/messages/search", {
      params: {
        mailbox,
        q: searchParams.q || "",
        from: searchParams.from || "",
        to: searchParams.to || "",
        subject: searchParams.subject || "",
        p: page,
        pageSize,
      },
    });
  },

  // Get specific email and mark as read
  getEmail: async (mailbox, emailId) => {
    return await mailClientAxios.get(`/messages/${encodeURIComponent(mailbox)}/${emailId}`);
  },

  // Change email flags (unified API for all flag operations)
  changeFlags: async (mailbox, emailId, addFlags = [], removeFlags = []) => {
    return await mailClientAxios.post(`/messages/${encodeURIComponent(mailbox)}/${emailId}/change-flags`, {
      addFlags,
      removeFlags,
    });
  },

  // Convenience methods using changeFlags
  markAsRead: async (mailbox, emailId) => {
    return await mailClientAPI.changeFlags(mailbox, emailId, ["\\Seen"], []);
  },

  markAsUnread: async (mailbox, emailId) => {
    return await mailClientAPI.changeFlags(mailbox, emailId, [], ["\\Seen"]);
  },

  starEmail: async (mailbox, emailId) => {
    return await mailClientAPI.changeFlags(mailbox, emailId, ["\\Flagged"], []);
  },

  unstarEmail: async (mailbox, emailId) => {
    return await mailClientAPI.changeFlags(mailbox, emailId, [], ["\\Flagged"]);
  },

  // Archive email
  archiveEmail: async (mailbox, emailId) => {
    return await mailClientAxios.post(`/messages/${encodeURIComponent(mailbox)}/${emailId}/archive`);
  },

  // Mark email as spam
  markAsSpam: async (mailbox, emailId) => {
    return await mailClientAxios.post(`/messages/${encodeURIComponent(mailbox)}/${emailId}/spam`);
  },

  // Move email to trash
  trashEmail: async (mailbox, emailId) => {
    return await mailClientAxios.post(`/messages/${encodeURIComponent(mailbox)}/${emailId}/trash`);
  },

  // Restore email from trash (move to INBOX)
  restoreEmail: async (mailbox, emailId) => {
    return await mailClientAxios.post(`/messages/${encodeURIComponent(mailbox)}/${emailId}/restore`);
  },

  // Permanently delete email (add Deleted flag and expunge)
  permanentDeleteEmail: async (mailbox, emailId) => {
    return await mailClientAxios.post(`/messages/${encodeURIComponent(mailbox)}/${emailId}/permanent-delete`);
  },

  // Trust sender for image display
  trustSender: async (mailbox, emailId, senderEmail) => {
    return await mailClientAxios.post(`/messages/${encodeURIComponent(mailbox)}/${emailId}/trust-sender`, {
      senderEmail,
    });
  },

  // Download attachment
  downloadAttachment: async (mailbox, emailId, partId) => {
    return await mailClientAxios.get(`/messages/${encodeURIComponent(mailbox)}/${emailId}/attachments/${partId}`, {
      responseType: 'blob', // Important for binary data
    });
  },

  // Summarize email thread
  summarizeThread: async (mailbox, emailId, emailData) => {
    return await mailClientAxios.post("/summarize", {
      mailbox,
      emailId,
      emailData,
    });
  },

  // Generate automated reply suggestions
  generateReply: async (mailbox, emailId, emailData) => {
    return await mailClientAxios.post("/generate-reply", {
      mailbox,
      emailId,
      emailData,
    });
  },

  // Send email
  sendEmail: async (formData) => {
    const token = getToken();
    const response = await axios.post(
      `${BASE_API_URL}/mail/client/send`,
      formData,
      {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Schedule email
  scheduleEmail: async (formData) => {
    const token = getToken();
    const response = await axios.post(
      `${BASE_API_URL}/mail/client/schedule`,
      formData,
      {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Compose with AI
  composeWithAI: async (data) => {
    return await mailClientAxios.post("/compose-with-ai", data);
  },

  // Save draft
  saveDraft: async (formData) => {
    const token = getToken();
    const response = await axios.post(
      `${BASE_API_URL}/mail/client/drafts`,
      formData,
      {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Get draft
  getDraft: async (mailbox, emailId) => {
    return await mailClientAxios.get(`/drafts/${encodeURIComponent(mailbox)}/${emailId}`);
  },

  // Delete draft
  deleteDraft: async (mailbox, emailId) => {
    return await mailClientAxios.delete(`/drafts/${encodeURIComponent(mailbox)}/${emailId}`);
  },

  // Labels API
  getLabels: async () => {
    return await mailClientAxios.get("/labels");
  },

  createLabel: async (name, color = "#3B82F6") => {
    return await mailClientAxios.post("/labels", { name, color });
  },

  updateLabel: async (id, name, color) => {
    const body = {};
    if (name !== undefined) body.name = name;
    if (color !== undefined) body.color = color;
    return await mailClientAxios.put(`/labels/${id}`, body);
  },

  deleteLabel: async (id) => {
    return await mailClientAxios.delete(`/labels/${id}`);
  },

  applyLabelsToEmail: async (mailbox, emailId, labelIds = [], removeLabelIds = []) => {
    return await mailClientAxios.post(`/messages/${encodeURIComponent(mailbox)}/${emailId}/labels`, {
      labelIds,
      removeLabelIds,
    });
  },

  // Export emails
  exportEmails: async (selectedFolders, dateRange, exportEmail) => {
    return await mailClientAxios.post("/export", {
      selectedFolders,
      dateRange,
      exportEmail,
    });
  },

  // Import emails
  importEmails: async (file, importStrategy) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('importStrategy', importStrategy);
    
    const response = await axios.post(
      `${BASE_API_URL}/mail/client/import`,
      formData,
      {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Rewrite email draft
  rewriteEmail: async (emailBody, rewriteType) => {
    return await mailClientAxios.post("/rewrite", {
      emailBody,
      rewriteType,
    });
  },

  // Upload file to CDN
  uploadFile: async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(
      `${BASE_API_URL}/mail/client/upload`,
      formData,
      {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },
};

export default mailClientAPI;

